/**
 * Common TypeScript types to replace 'any' throughout the application
 * Provides type-safe alternatives for generic patterns
 */

import type {
  User,
  WhoAmIResponse,
} from '../api/data-contracts';

// ============================================================================
// Re-export API Types for Convenience
// ============================================================================

/**
 * Canonical session user model used by frontend auth state.
 * Derived from who-am-i response with compatibility fields for legacy UI code.
 */
export interface AppUser extends Omit<WhoAmIResponse, 'role'> {
  /**
   * Role is widened to include superadmin until backend schema is updated.
   */
  role: WhoAmIResponse['role'] | 'superadmin';
  /**
   * Alias for user_id used by legacy components.
   */
  id: string;
  /**
   * Not guaranteed by who-am-i endpoint.
   */
  created_at?: string;
  /**
   * Optional profile/company fields used by account settings UI.
   */
  company?: string | null;
  organizationName?: string;
  is_active?: boolean;
}

/**
 * Backwards-compatible alias retained for existing components.
 */
export type AuthUser = AppUser;

/**
 * Normalize who-am-i response into canonical session user shape.
 */
export const mapWhoAmIToAppUser = (response: WhoAmIResponse): AppUser => {
  return {
    ...response,
    id: response.user_id,
    role: response.role,
    created_at: undefined,
    company: null,
    organizationName: undefined,
    is_active: true,
  };
};

export const isAppUser = (value: unknown): value is AppUser => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return typeof candidate.user_id === 'string' && typeof candidate.id === 'string';
};

/**
 * Re-export permission types
 */
export type { UserPermissions, PermissionDetail } from '../api/data-contracts';

// ============================================================================
// Generic Types
// ============================================================================

/**
 * Represents any valid JSON value
 * Use this for data that can be serialized/deserialized as JSON
 */
export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonObject
  | JsonArray;

export interface JsonObject {
  [key: string]: JsonValue;
}

export type JsonArray = JsonValue[];

/**
 * JSON value that can be undefined (useful for optional fields)
 * Use this for form fields or API responses with optional values
 */
export type OptionalJsonValue = JsonValue | undefined;

/**
 * Unknown record type for dynamic object structures
 * Prefer this over 'any' for objects with unknown keys
 */
export type UnknownRecord = Record<string, unknown>;

/**
 * Type-safe error object
 */
export interface ErrorObject {
  message: string;
  code?: string;
  status?: number;
  details?: UnknownRecord;
}

// ============================================================================
// Auth Types
// ============================================================================

/**
 * Login handler function signature
 * Note: AuthUser is re-exported from WhoAmIResponse above
 */
export type LoginHandler = (token: string, user: User) => void;

/**
 * Provider for authentication redirects
 */
export interface AuthProvider {
  provider_type: 'entra' | 'google' | 'okta' | 'local';
  is_primary: boolean;
  config?: UnknownRecord;
}

/**
 * MSAL token exchange additional data
 */
export interface MsalAdditionalData {
  azure_groups?: string[];
  azure_groups_resolved?: Array<{
    id: string;
    displayName: string;
  }>;
}

/**
 * Entra configuration
 */
export interface EntraConfig {
  clientId: string;
  authority: string;
  redirectUri: string;
  scopes?: string[];
}

// ============================================================================
// System Settings Types
// ============================================================================

/**
 * System configuration settings
 */
export interface SystemSettings {
  organization_name?: string;
  support_email?: string;
  max_file_size_mb?: number;
  allowed_file_types?: string[];
  session_timeout_minutes?: number;
  [key: string]: OptionalJsonValue;
}

/**
 * Branding configuration
 */
export interface BrandingSettings {
  logo_url?: string;
  primary_color?: string;
  secondary_color?: string;
  company_name?: string;
  favicon_url?: string;
  [key: string]: OptionalJsonValue;
}

/**
 * Security configuration
 */
export interface SecuritySettings {
  require_email_verification?: boolean;
  password_min_length?: number;
  password_require_special_char?: boolean;
  password_require_number?: boolean;
  session_timeout_minutes?: number;
  max_login_attempts?: number;
  lockout_duration_minutes?: number;
  [key: string]: OptionalJsonValue;
}

/**
 * Feature flags configuration
 */
export interface FeatureSettings {
  enable_chat?: boolean;
  enable_documents?: boolean;
  enable_faq?: boolean;
  enable_groups?: boolean;
  enable_search?: boolean;
  [key: string]: OptionalJsonValue;
}

/**
 * Document settings
 */
export interface DocumentSettings {
  max_file_size_mb?: number;
  allowed_file_types?: string[];
  enable_auto_processing?: boolean;
  enable_ocr?: boolean;
  default_visibility?: 'private' | 'organization' | 'public';
  [key: string]: OptionalJsonValue;
}

/**
 * Organization/System configuration
 */
export interface Organization {
  id: string;
  name: string;
  slug?: string;
  description?: string;
  settings?: {
    branding?: BrandingSettings;
    features?: FeatureSettings;
    limits?: {
      max_users?: number | null;
      max_documents?: number | null;
      max_storage_gb?: number | null;
      max_file_size_mb?: number;
    };
    security?: SecuritySettings;
  };
  metadata?: {
    contact_email?: string | null;
    contact_phone?: string | null;
    timezone?: string;
    language?: string;
    country?: string;
  };
  status?: string;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
}

// ============================================================================
// Form Data Types
// ============================================================================

/**
 * Generic form field value
 */
export type FormFieldValue = string | number | boolean | null | undefined | string[];

/**
 * User form data for creation/update
 */
export interface UserFormData {
  email: string;
  full_name?: string;
  password?: string;
  role?: string;
  groups?: string[];
  company?: string;
  [key: string]: FormFieldValue;
}

/**
 * Document form data
 */
export interface DocumentFormData {
  title?: string;
  description?: string;
  content?: string;
  file?: File;
  groups?: string[];
  tags?: string[];
  source_type?: string;
  [key: string]: FormFieldValue | File | undefined;
}

/**
 * Configuration form data for various config types
 */
export interface ConfigFormData {
  name?: string;
  description?: string;
  provider?: string;
  config?: UnknownRecord;
  is_active?: boolean;
  [key: string]: FormFieldValue | UnknownRecord | undefined;
}

// ============================================================================
// Chat Types
// ============================================================================

/**
 * Chat model configuration
 */
export interface ChatModelConfig {
  temperature?: number;
  max_tokens?: number;
  top_p?: number;
  frequency_penalty?: number;
  presence_penalty?: number;
  [key: string]: OptionalJsonValue;
}

/**
 * Available chat model
 */
export interface ChatModel {
  id: string;
  name: string;
  provider: string;
  config: ChatModelConfig;
  is_default?: boolean;
}

/**
 * Chat chunk with optional context
 */
export interface ChatChunk {
  content: string;
  context?: JsonArray;
}

/**
 * Chat chunk handler
 */
export type ChunkHandler = (chunk: string, context?: JsonArray) => void;

// ============================================================================
// Query/Mutation Types
// ============================================================================

/**
 * React Query mutation function signature
 */
export type MutationFunction<TData = unknown, TVariables = unknown> = (
  variables: TVariables
) => Promise<TData>;

/**
 * Generic mutation result
 */
export interface MutationResult<TData = unknown> {
  mutate: (variables: unknown) => void;
  mutateAsync: (variables: unknown) => Promise<TData>;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  data: TData | undefined;
}

/**
 * Query parameters
 */
export interface QueryParams {
  page?: number;
  limit?: number;
  search?: string;
  filters?: UnknownRecord;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  [key: string]: OptionalJsonValue | UnknownRecord;
}

// ============================================================================
// HTTP/API Types
// ============================================================================

/**
 * HTTP request data
 */
export type HttpRequestData = JsonValue | FormData | Blob;

/**
 * HTTP response data
 */
export type HttpResponseData = JsonValue;

/**
 * API error response structure
 */
export interface ApiErrorResponse {
  message: string;
  code?: string;
  status: number;
  data?: UnknownRecord;
}

/**
 * HTTP error with response
 */
export interface HttpError extends Error {
  response?: {
    status: number;
    statusText: string;
    data?: ApiErrorResponse;
  };
  code?: string;
}

// ============================================================================
// Event Handlers
// ============================================================================

/**
 * Generic event handler
 */
export type EventHandler<T = unknown> = (event: T) => void;

/**
 * Form change handler
 */
export type FormChangeHandler<T extends FormFieldValue = FormFieldValue> = (
  field: string,
  value: T
) => void;

/**
 * Async event handler
 */
export type AsyncEventHandler<T = unknown, R = void> = (event: T) => Promise<R>;

// ============================================================================
// Component Props Types
// ============================================================================

/**
 * Generic update handler
 */
export type UpdateHandler<T = UnknownRecord> = (updates: T) => Promise<void>;

/**
 * Generic submit handler
 */
export type SubmitHandler<T = UnknownRecord> = (data: T, isEdit: boolean) => Promise<void>;

// ============================================================================
// Logger Types
// ============================================================================

/**
 * Logger data structure
 */
export interface LogData {
  [key: string]: unknown;
}

/**
 * Performance metric
 */
export interface PerformanceMetric {
  name: string;
  value: number;
  rating?: 'good' | 'needs-improvement' | 'poor';
  [key: string]: OptionalJsonValue;
}

// ============================================================================
// Validation Types
// ============================================================================

/**
 * Validation error map
 */
export type ValidationErrors = Record<string, string>;

/**
 * Form validation result
 */
export interface ValidationResult {
  isValid: boolean;
  errors: ValidationErrors;
}

// ============================================================================
// Document Sources Types
// ============================================================================

/**
 * Extended connection response with additional client-side fields
 * Used across document source components
 */
export interface ExtendedConnectionResponse {
  id: string;
  provider_config_id: string;
  name: string;
  enabled: boolean;
  sync_interval: string;
  file_filters?: string[];
  max_file_size?: number;
  retry_attempts?: number;
  retry_delay?: number;
  debounce_seconds?: number;
  folder_path?: string;
  share_url?: string | null;
  needs_reconnect?: boolean;
  last_sync_at?: string | null;
  provider_type?: string;
  provider_key?: string;
  provider_name?: string;
  status?: string;
  user_provider_config?: Record<string, any>;
}
